/**
 * BilingualField
 * ──────────────
 * İki dilli (TR/EN) input / textarea alanı için tab'lı kompakt komponent.
 * Admin formlarında ad, açıklama, kategori gibi alanlarda kullanılır.
 *
 * Props:
 *   valueTr, valueEn      — current values
 *   onChangeTr, onChangeEn — handlers
 *   label                  — ortak label
 *   required, error, placeholder{tr,en}
 *   as = 'input' | 'textarea'
 *   rows                   — textarea için
 *   className              — outer div sınıfı
 *   testIdBase             — data-testid prefix (ör: "product-name")
 *   focusRingClass         — "focus:ring-orange-200" gibi
 */
import React, { useState } from 'react';

export default function BilingualField({
  valueTr = '',
  valueEn = '',
  onChangeTr,
  onChangeEn,
  label,
  required = false,
  error,
  placeholderTr = '',
  placeholderEn = '',
  as = 'input',
  rows = 3,
  className = '',
  testIdBase,
  focusRingClass = 'focus:ring-orange-200',
  errorBorderClass = 'border-red-300 focus:ring-red-200',
}) {
  const [lang, setLang] = useState('tr');

  const baseInput = `w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${error ? errorBorderClass : `border-gray-200 ${focusRingClass}`}`;

  const commonProps = {
    value: lang === 'tr' ? (valueTr || '') : (valueEn || ''),
    onChange: (e) => (lang === 'tr' ? onChangeTr(e.target.value) : onChangeEn(e.target.value)),
    placeholder: lang === 'tr' ? placeholderTr : placeholderEn,
    className: as === 'textarea' ? `${baseInput} resize-none` : baseInput,
    'data-testid': testIdBase ? `${testIdBase}-${lang}-input` : undefined,
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-sm font-semibold text-gray-700">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 text-[10px] font-bold tracking-widest" data-testid={testIdBase ? `${testIdBase}-lang-toggle` : undefined}>
          <button
            type="button"
            onClick={() => setLang('tr')}
            className={`px-2.5 py-1 rounded-md transition-colors ${lang === 'tr' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={testIdBase ? `${testIdBase}-tab-tr` : undefined}
          >
            TR
          </button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={`px-2.5 py-1 rounded-md transition-colors ${lang === 'en' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={testIdBase ? `${testIdBase}-tab-en` : undefined}
          >
            EN
          </button>
        </div>
      </div>
      {as === 'textarea'
        ? <textarea {...commonProps} rows={rows} />
        : <input type="text" {...commonProps} />
      }
      <div className="flex items-center justify-between mt-1">
        {error
          ? <p className="text-xs text-red-500">{error}</p>
          : <p className="text-[10px] text-gray-400">
              {lang === 'tr'
                ? 'TR içerik — Türkçe UI kullanıcılarına gösterilir.'
                : 'EN content — shown to English UI users.'}
            </p>
        }
        <div className="flex items-center gap-1 text-[10px] text-gray-400">
          <span className={valueTr ? 'font-semibold text-gray-600' : ''}>TR {valueTr ? '✓' : '·'}</span>
          <span>·</span>
          <span className={valueEn ? 'font-semibold text-gray-600' : ''}>EN {valueEn ? '✓' : '·'}</span>
        </div>
      </div>
    </div>
  );
}
